// src/contexts/InsiderBuyTogetherContext/InsiderBuyTogetherContext.tsx
import React, { createContext, useContext, useState, useCallback } from 'react'

export interface Buyable {
  id: string
  price: number
  raw: any
}

interface BuyTogetherSelectionContextType {
  selected: Record<string, Buyable>
  toggleSelection: (item: Buyable) => void
  isSelected: (id: string) => boolean
  clearSelection: () => void
}

const BuyTogetherSelectionContext = createContext<
  BuyTogetherSelectionContextType | undefined
>(undefined)

export const BuyTogetherSelectionProvider: React.FC<{
  children: React.ReactNode
}> = ({ children }) => {
  const [selected, setSelected] = useState<Record<string, Buyable>>({})

  const toggleSelection = useCallback((item: Buyable) => {
    setSelected((prev) => {
      if (prev[item.id]) {
        const { [item.id]: _, ...rest } = prev
        return rest
      }
      return {
        ...prev,
        [item.id]: item,
      }
    })
  }, [])

  const isSelected = useCallback(
    (id: string) => {
      return Boolean(selected[id])
    },
    [selected]
  )

  const clearSelection = useCallback(() => {
    setSelected({})
  }, [])

  return (
    <BuyTogetherSelectionContext.Provider
      value={{
        selected,
        toggleSelection,
        isSelected,
        clearSelection,
      }}
    >
      {children}
    </BuyTogetherSelectionContext.Provider>
  )
}

export const useBuyTogetherSelection = (): BuyTogetherSelectionContextType => {
  const context = useContext(BuyTogetherSelectionContext)

  if (!context) {
    throw new Error(
      'useBuyTogetherSelection must be used within a BuyTogetherSelectionProvider'
    )
  }

  return context
}
